import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, combineLatest } from 'rxjs';
import { catchError, switchMap, take, concatMap } from 'rxjs/operators';
import { HttpStatusCode } from '@angular/common/http';
import { PostcodeChildTicketVerifyActions } from '../../actions';
import { CheckoutService } from '../../services';
import { VerifyChildPostcodeTicket } from '../../types';
import { formatPostcodeTicketVerify } from '../../util';
import {
  PostcodeTicketVerifyOperationResponse,
  PostcodeTicketVerifyRequest,
} from '../../client/src';
import { ApiError } from '@modules/ticket-fares-store';
import { isApiError } from '@nx-ng/util';
import { LoggingService } from '@nx-ng/types';
import { LOGGER } from '@modules/core';

@Injectable()
export class PostcodeChildTicketVerifyEffects {
  verifyMultipleChildTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PostcodeChildTicketVerifyActions.verifyPostcodeChildTickets),
      switchMap(
        ({ verifyList }: { verifyList: VerifyChildPostcodeTicket[] }) => {
          return verifyList.map((listItem: VerifyChildPostcodeTicket) =>
            PostcodeChildTicketVerifyActions.verifyPostcodeChildTicket({
              verify: listItem,
            })
          );
        }
      )
    )
  );

  verifyChildTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PostcodeChildTicketVerifyActions.verifyPostcodeChildTicket),
      concatMap(({ verify }: { verify: VerifyChildPostcodeTicket }) => {
        const request: PostcodeTicketVerifyRequest =
          formatPostcodeTicketVerify(verify);
        return combineLatest([
          of(verify),
          this.checkoutService.postcodeVerify(request),
        ]).pipe(
          take(1),
          switchMap(
            ([verify, response]: [
              verify: VerifyChildPostcodeTicket,
              response: PostcodeTicketVerifyOperationResponse
            ]) => {
              return of(
                PostcodeChildTicketVerifyActions.checkoutAPI_verifyPostcodeChildTicketSuccess(
                  {
                    verify,
                    response,
                  }
                )
              );
            }
          ),
          catchError((error) => {
            const apiError: ApiError | string =
              error?.response?.parsedBody || error;

            if (
              isApiError(apiError) &&
              apiError.code === HttpStatusCode.NotFound
            ) {
              return of(
                PostcodeChildTicketVerifyActions.checkoutAPI_verifyPostcodeChildTicketSuccess(
                  {
                    verify,
                    response: {
                      status: 'not-verified',
                    },
                  }
                )
              );
            }

            this.logger.logApiError(
              {
                apiError,
              },
              error
            );

            return of(
              PostcodeChildTicketVerifyActions.checkoutAPI_verifyPostcodeChildTicketFailure(
                {
                  error,
                  verify,
                }
              )
            );
          })
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly checkoutService: CheckoutService,
    @Inject(LOGGER) private logger: LoggingService
  ) {}
}
